// One-way adapter between the SavedDashboard `layout` JSON blob and the
// in-memory DashboardLayout. The wire blob is freeform JSON so legacy v1
// records (just `{ version: 1 }`) coexist with v2 widget layouts. When we
// can't recognise the blob shape we fall back to the default layout rather
// than throwing — a malformed record shouldn't take down the page.

import {
  DEFAULT_DASHBOARD_LAYOUT,
  WIDGET_TYPES,
  type DashboardLayout,
  type WidgetInstance,
  type WidgetType,
} from "./types";

const WIDGET_TYPE_SET = new Set<string>(WIDGET_TYPES);

function isWidgetType(value: unknown): value is WidgetType {
  return typeof value === "string" && WIDGET_TYPE_SET.has(value);
}

function asNumber(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

export function readLayoutFromBlob(
  blob: { [key: string]: unknown } | null | undefined,
): DashboardLayout {
  if (blob === null || blob === undefined) {
    return DEFAULT_DASHBOARD_LAYOUT;
  }
  if (blob.version !== 2) {
    return DEFAULT_DASHBOARD_LAYOUT;
  }
  const rawWidgets = blob.widgets;
  if (!Array.isArray(rawWidgets)) {
    return DEFAULT_DASHBOARD_LAYOUT;
  }
  const widgets: WidgetInstance[] = [];
  for (const raw of rawWidgets) {
    if (typeof raw !== "object" || raw === null) continue;
    const r = raw as Record<string, unknown>;
    if (!isWidgetType(r.type)) continue;
    const id = typeof r.id === "string" && r.id.length > 0 ? r.id : `${r.type}-${widgets.length}`;
    widgets.push({
      id,
      type: r.type,
      x: asNumber(r.x, 0),
      y: asNumber(r.y, 0),
      w: asNumber(r.w, 3),
      h: asNumber(r.h, 2),
    });
  }
  if (widgets.length === 0) {
    return DEFAULT_DASHBOARD_LAYOUT;
  }
  return { version: 2, widgets };
}

export function writeLayoutToBlob(layout: DashboardLayout): {
  [key: string]: unknown;
} {
  return {
    version: 2,
    widgets: layout.widgets.map((w) => ({
      id: w.id,
      type: w.type,
      x: w.x,
      y: w.y,
      w: w.w,
      h: w.h,
    })),
  };
}
